import './hookField.css'
import { isLogicBlockField } from '../../../_CONTAINERS/Parameters/parametersHelpers'
import InputCheckbox from '../../FORMS/InputCheckbox/InputCheckbox'
import { translate } from '../../../translation'
import { classNames, isNumberArray, mayJsonParse, useCallPipedriveApi } from '../../../utils/helpers'
import LogicBlocks from '../../LOGICBLOCK/LogicBlocks'
import ConditionMaker from '../../ConditionMaker/ConditionMaker'
import { additionalFieldsData } from '../../../appConstante'
import { HookField as HookFieldType, Block, GroupedStages } from 'Types'
import { hookFieldStore } from '_STORES/HookField'
import { hookStore } from '_STORES/Hooks'
import { appDataStore } from '_STORES/AppData'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { getBlockExemple } from '_COMPONENTS/LOGICBLOCK/VariableBlock'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useLoadPipedriveUsers } from 'utils/pipedrive'
import HoverToolType from '_COMPONENTS/GENERAL/HoverToolType/HoverToolType'
import { useNotification } from '_CONTEXT/hook/contextHook'

interface Props {
  hookField: HookFieldType
}

const HookField = ({ hookField }: Props) => {

  const callPipedriveApi = useCallPipedriveApi()
  const notification = useNotification()

  const [open, setOpen] = useState<boolean>(false)

  // Format the displayed value based on its type
  const getDisplayValue = (hookField: HookFieldType): string | JSX.Element => {
    if (!hookFieldStore.hasValue(hookField)) {
      return <span>⚠️ you need to set a value</span>
    }

    const value = hookField.value

    // Handle boolean values
    if (typeof value === "boolean") {
      return value ? "Yes" : "No"
    }

    // Handle array of blocks (LogicBlocks)
    if (Array.isArray(value) && value.length && typeof value[0] !== "number") {
      return getBlockExemple(value[0])
    }

    // Handle array of numbers/strings
    if (Array.isArray(value) && value.length) {
      return value.map(v => mayJsonParse(`${v}`, v)).join(', ')
    }

    // Handle other values (string, number, etc.)
    return mayJsonParse(`${value}`, value)
  }

  const selectedHook = useMemo(() => hookStore.getHook(hookField.hookId), [hookField])

  const fieldData = selectedHook
    ? additionalFieldsData[selectedHook.category][hookField.pipedrive.field_code]
    : undefined

  // Avertissement attaché à une option précise (ex: status "deleted" sur un deal)
  const getOptionWarning = (optionId: string | number): JSX.Element | undefined =>
    fieldData?.optionsWarning?.[`${optionId}`]

  const selectedOptionWarning = (typeof hookField.value === "string" || typeof hookField.value === "number")
    ? getOptionWarning(hookField.value)
    : undefined

  const updateHookField = (key: keyof HookFieldType, value: any) => {
    if (key === 'enabled') {
      if (value === true) {
        setOpen(_ => true)
      } else {
        setOpen(_ => false)
      }
    }
    selectedHook && hookStore.updateHookField(selectedHook, hookField.id, { [key]: value });
  };

  const updateOptionHookField = (key: keyof HookFieldType["condition"], value: any) => {
    selectedHook && hookStore.updateHookField(selectedHook, hookField.id, { condition: { ...hookField.condition, [key]: value } });
  };

  const loadPipedriveUsers = useLoadPipedriveUsers()

  useEffect(() => {
    if (hookField.enabled && hookFieldStore.isRequired(hookField)) {
      updateHookField("enabled", true)
    }
  }, [])

  const loadPipedriveStages = async (e: FormEvent) => {
    const pipelines = await callPipedriveApi("pipelines", null, null, { limit: 500 }, e)
    callPipedriveApi("stages", null, null, null, e)
      .then(res => {
        if (res) {
          const newAppDataStore = appDataStore.appData
          newAppDataStore.parameters.pipedrive.stages = res.data.data.map((stage: any) => ({
            ...stage,
            pipeline_name: pipelines?.data?.data?.find((pipeline: any) => pipeline.id === stage.pipeline_id)?.name ?? undefined
          }))
          appDataStore.setAppData(newAppDataStore)
        }
      })
  }

  const selectOption = (id: number) => {
    if (
      hookField.pipedrive.field_type === "enum" ||
      hookField.pipedrive.field_type === "user" ||
      hookField.pipedrive.field_type === "status" ||
      hookField.pipedrive.field_type === "visible_to" ||
      hookField.pipedrive.field_type === "stage"
    ) {
      updateHookField('value', id)
    } else if (hookField.pipedrive.field_type === "set") {
      if (Array.isArray(hookField.value)) {
        if (isNumberArray(hookField.value) && hookField.value.includes(id)) {
          updateHookField('value', hookField.value.filter(val => val !== id));
        } else {
          updateHookField('value', [...hookField.value, id]);
        }
      } else {
        updateHookField('value', [id]);
      }
    }
  }

  const selectBoolean = (value: boolean) => {
    updateHookField('value', value)
  }

  const renderStages = () => {
    // Typage explicite du résultat de reduce
    const groupedStages: GroupedStages = appDataStore.appData.parameters.pipedrive.stages.reduce((acc, item) => {
      const { pipeline_name } = item;
      if (!acc[pipeline_name]) {
        acc[pipeline_name] = [];
      }
      acc[pipeline_name].push(item);
      return acc;
    }, {} as GroupedStages);  // Type de l'accumulateur explicitement défini

    // Transformation de l'objet en tableau
    const groupedStagesArray = Object.entries(groupedStages).map(([pipeline_name, stages]) => ({
      pipeline_name,
      stages
    }));

    // Rendu JSX
    return groupedStagesArray.map(group => (
      <div key={group.pipeline_name} className='flex m-b-10 column'>
        <div className='m-b-5 strong-1'>{group.pipeline_name}</div>
        <div className='flex flex-wrap gap-1'>
          {
            group.stages.map(stage => (
              <div
                key={stage.id}
                onClick={() => selectOption(stage.id)}
                className={`pipedrive-option-field ${hookField.value === stage.id ? "selected" : ""}`}
                style={{ backgroundColor: "white" }}
              >
                {stage.name}
              </div>
            ))
          }
        </div>
      </div>
    ));
  };

  return (
    <div className='pipedrive-field'>
      <div className='flex items-center'>
        <InputCheckbox
          checked={hookField.enabled}
          onChange={(value) => {
            if (hookFieldStore.isRequired(hookField)) {
              notification.addNotification({
                content: "This field is required",
                error: true
              })
              return
            }
            updateHookField("enabled", value)
          }}
        />

        <div
          onClick={_ => setOpen(prv => !prv)}
          className='flex justify-between items-center w-full cursor-pointer' >
          <div className='font-medium text-base'>
            {hookFieldStore.isRequired(hookField)
              ? <HoverToolType
                content={<span>🚨</span>}
                toolTip="This field is required"
              />
              : null}
            {`${hookField.pipedrive.field_name}`}
          </div>

          <div className='flex gap-5'>
            {hookField.enabled && selectedOptionWarning
              ? <HoverToolType
                content={<span>⚠️</span>}
                toolTip={selectedOptionWarning}
              />
              : null}
            {hookField.enabled
              ? <div className='italic'>
                <div className={
                  classNames(
                    "transition-opacity opacity-100"
                  )}
                  style={{ whiteSpace: 'pre-line' }}>
                  {getDisplayValue(hookField)}
                </div>

              </div>
              : null}
            {hookField.enabled
              ? <div
                className={`${open ? "rotate-0" : "-rotate-90"} transition-transform cursor-pointer`} >
                <ChevronDownIcon width={15} />
              </div>
              : null}
          </div>
        </div>
      </div>
      {selectedHook && hookField.enabled && open
        ? <div>
          {fieldData?.info
            ? <p>{fieldData.info}</p>
            : null}

          {selectedOptionWarning
            ? <div className='w2p-warning m-t-10'>{selectedOptionWarning}</div>
            : null}

          {/* FIELDTYPE SET */}
          {hookField.pipedrive.field_type === "set"
            ? hookField.pipedrive.options && hookField.pipedrive.options.length
              ? <div className='pipedrive-option-field-container'> {
                hookField.pipedrive.options.map((option, index) =>
                  <div
                    key={index}
                    onClick={e => selectOption(option.id)}
                    className={`pipedrive-option-field ${(isNumberArray(hookField.value) && hookField.value.includes(option.id))
                      ? "selected"
                      : ""}`}
                    style={{ backgroundColor: option.color ? option.color : "white" }}
                  >{option.label}</div>
                )
              }
              </div>
              : <div className='m-t-10'>{translate("No options found for this field. If you’ve just added some in Pipedrive, reload custom fields at the bottom of the page.")}</div>
            : null
          }
          {/* FIELDTYPE ENUM */}
          {(hookField.pipedrive.field_type === "enum" || hookField.pipedrive.field_type === "status" || hookField.pipedrive.field_type === "visible_to")
            ? hookField.pipedrive.options
              ? <div className='pipedrive-option-field-container'> {
                hookField.pipedrive.options.map((option, index) =>
                  <div
                    key={index}
                    onClick={e => selectOption(option.id)}
                    className={`pipedrive-option-field ${hookField.value === option.id ? "selected" : ""}`}
                    style={{ backgroundColor: option.color ? option.color : "white" }}
                  >
                    {getOptionWarning(option.id) ? <span className='mr-1'>⚠️</span> : null}
                    {option.label}
                  </div>
                )
              }
              </div>
              : null
            : null
          }
          {/* FIELDTYPE VARCHAR / ADRESS */}
          {isLogicBlockField(hookField.pipedrive)
            ? <LogicBlocks
              fieldCondition={hookField.condition}
              setter={(logicBlocks) => updateHookField("value", logicBlocks)}
              defaultLogicBlocks={hookField.value as Block[]}
              source={selectedHook && selectedHook.source}
            />
            : null
          }


          {/* FIELDTYPE USER */}
          {(hookField.pipedrive.field_type === "user")
            ? <>
              <div className='pipedrive-option-field-container'> {
                appDataStore.appData.parameters.pipedrive.users.map((user, index) =>
                  <div
                    key={index}
                    onClick={e => selectOption(user.id)}
                    className={`pipedrive-option-field ${hookField.value === user.id ? "selected" : ""}`}
                    style={{ backgroundColor: "white" }}
                  >{user.name}</div>
                )
              }
              </div>
              <form onSubmit={e => loadPipedriveUsers(e)}>
                <button
                  className='light-button'
                >
                  {appDataStore.appData.parameters.pipedrive.users.length
                    ? translate("Reload pipedrive's users")
                    : translate("Load pipedrive's users")
                  }
                </button>
              </form>
            </>
            : null
          }

          {/* FIELDTYPE STAGE */}
          {(hookField.pipedrive.field_type === "stage")
            ? <>
              <div className='pipedrive-option-field-container'> {
                renderStages()
              }
              </div>
              <form onSubmit={e => loadPipedriveStages(e)}>
                <button
                  className='light-button'
                >
                  {appDataStore.appData.parameters.pipedrive.stages.length
                    ? translate("Reload pipedrive's stages")
                    : translate("Load pipedrive's stages")}
                </button>
              </form>
            </>
            : null
          }

          {/* FIELDTYPE BOOLEAN */}
          {(hookField.pipedrive.field_type === "boolean")
            ? <div className='pipedrive-option-field-container'>
              <div
                onClick={() => selectBoolean(true)}
                className={`pipedrive-option-field ${typeof hookField.value === "boolean" && hookField.value === true ? "selected" : ""}`}
                style={{ backgroundColor: "white" }}
              >
                Yes
              </div>
              <div
                onClick={() => selectBoolean(false)}
                className={`pipedrive-option-field ${typeof hookField.value === "boolean" && hookField.value === false ? "selected" : ""}`}
                style={{ backgroundColor: "white" }}
              >
                No
              </div>
            </div>
            : null
          }

          <div>
            <h5 className='m-b-10 m-t-40 strong-1'>
              {translate("Condition")}
            </h5>
            <div className='flex flex-col gap-2'>
              <div className='mb-2'>
                {isLogicBlockField(hookField.pipedrive)
                  ? <>
                    <ConditionMaker
                      logicBlockCondition={hookField.condition.logicBlock}
                      setter={condition => updateOptionHookField("logicBlock", condition)}
                    />
                  </>
                  : null
                }
              </div>

              <div>
                <label className='flex items-center gap-1 cursor-pointer'>
                  <input
                    type='checkbox'
                    // className='w2p-normal-checkbox'
                    // value={''}
                    onChange={e => updateOptionHookField("SkipOnExist", e.target.checked)}
                    checked={hookField.condition.SkipOnExist ?? false}
                  />
                  {translate("Do not update if there is already a value for this field on Pipedrive")}
                </label>
              </div>

              <div>
                <label className='flex items-center gap-1'>
                  <input
                    type='checkbox'
                    onChange={e => updateOptionHookField("findInPipedrive", e.target.checked)}
                    checked={hookField.condition.findInPipedrive ?? false}
                  />
                  {selectedHook.category === "person"
                    ? translate(` If a person in Pipedrive already has this value for this field, link their WooCommerce account to that person. (only if it's not already linked).`)
                    : selectedHook.category === "organization"
                      ? translate(` If an organization in Pipedrive already has this value for this field, link their WooCommerce account to that organization. (only if it's not already linked).`)
                      : selectedHook.category === "deal"
                        ? translate(` If a deal in Pipedrive already has this value for this field, link the WooCommerce account to that deal. (only if it's not already linked).`)
                        : null
                  }
                </label>
              </div>
            </div>
          </div>
        </div>
        : null}
    </div>
  )
}

export default observer(HookField)